import { useState, useEffect, useRef } from "react";
import { RxArrowTopRight } from "react-icons/rx";
import { TbFileCertificate, TbBuilding, TbUsersGroup } from "react-icons/tb";
import { LuClock3 } from "react-icons/lu";
import { RiGraduationCapLine } from "react-icons/ri";
import { HiOutlineBriefcase } from "react-icons/hi";
import { IoGiftOutline } from "react-icons/io5";
import { PiGameControllerLight } from "react-icons/pi";
import Link from "next/link";
import { useRouter } from "next/router";
import { useWindowSize } from "@/utils/useWindowSize";
import { useTranslation } from "next-i18next";

const OurAdvantages = () => {
    const { t } = useTranslation();
    const { width } = useWindowSize();
    const router = useRouter();
    const isMobile = width ? width < 1024 : false;

    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [clickedIndex, setClickedIndex] = useState(null);
    const scrollRef = useRef(null);
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const dragScrollLeft = useRef(0);
    const didDrag = useRef(false);

    const advantages = [
        {
            id: 1,
            title: t("home-page.our-advantages-international-diploma-title"),
            description: t("home-page.our-advantages-international-diploma-description"),
            icon: TbFileCertificate,
        },
        {
            id: 2,
            title: t("home-page.our-advantages-duration-title"),
            description: t("home-page.our-advantages-duration-description"),
            icon: LuClock3,
        },
        {
            id: 3,
            title: t("home-page.our-advantages-facility-title"),
            description: t("home-page.our-advantages-facility-description"),
            icon: TbBuilding,
        },
        {
            id: 4,
            title: t("home-page.our-advantages-scholarship-title"),
            description: t("home-page.our-advantages-scholarship-description"),
            icon: RiGraduationCapLine,
        },
        {
            id: "employment",
            title: t("home-page.our-advantages-employment-title"),
            description: t("home-page.our-advantages-employment-description"),
            icon: HiOutlineBriefcase,
        },
        {
            id: "benefits",
            title: t("home-page.our-advantages-benefits-title"),
            description: t("home-page.our-advantages-benefits-description"),
            icon: IoGiftOutline,
        },
        {
            id: "community",
            title: t("home-page.our-advantages-community-title"),
            description: t("home-page.our-advantages-community-description"),
            icon: TbUsersGroup,
        },
        {
            id: "gamification",
            title: t("home-page.our-advantages-gamification-title"),
            description: t("home-page.our-advantages-gamification-description"),
            icon: PiGameControllerLight,
        },
    ];

    useEffect(() => {
        setHoveredIndex(null);
        setClickedIndex(null);
    }, [router.asPath]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const handleWheel = (e) => {
            if (window.innerWidth >= 1024) return;
            if (el.scrollWidth <= el.clientWidth) return;

            e.preventDefault();
            el.scrollLeft += e.deltaY;
        };

        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, []);

    const handleScrollMouseDown = (e) => {
        if (window.innerWidth >= 1024) return;
        isDragging.current = true;
        didDrag.current = false;
        dragStartX.current = e.clientX;
        dragScrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
    };

    const handleScrollMouseMove = (e) => {
        if (!isDragging.current || !scrollRef.current) return;
        const delta = e.clientX - dragStartX.current;
        if (Math.abs(delta) > 5) didDrag.current = true;
        scrollRef.current.scrollLeft = dragScrollLeft.current - delta;
    };

    const endScrollDrag = () => {
        isDragging.current = false;
    };

    const renderCard = (advantage, index) => {
        const isActive = isMobile
            ? clickedIndex === index
            : hoveredIndex === index;

        const handleMouseEnter = () => {
            if (!isMobile) setHoveredIndex(index);
        };

        const handleMouseLeave = () => {
            if (!isMobile) setHoveredIndex(null);
        };

        const handleClick = () => {
            if (isMobile && !didDrag.current) {
                setClickedIndex(clickedIndex === index ? null : index);
            }
        };

        const Icon = advantage.icon;

        return (
            <div
                key={advantage.id}
                className={`relative flex-shrink-0 w-[260px] sm:w-[280px] lg:w-auto lg:flex-shrink snap-start cursor-pointer rounded-[32px] p-6 aspect-square lg:aspect-square transition-all duration-300 overflow-hidden ${
                    isActive
                        ? "bg-main-pink text-white bg-[url('/images/metalic-bg.png')] bg-cover bg-center bg-no-repeat"
                        : "bg-[#F5F7F9] text-gray-700"
                }`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
            >
                <div
                    className={`flex flex-col justify-center gap-4 h-full transition-opacity duration-300 ${
                        isActive ? "opacity-0 invisible" : "opacity-100"
                    }`}
                >
                    <div className="w-[40px] h-[40px] md:w-[48px] md:h-[48px] rounded-full flex items-center justify-center bg-[#F6E5D9]">
                        <Icon className="w-[20px] md:w-[24px] h-[20px] md:h-[24px] text-main-pink" />
                    </div>
                    <h3 className="text-[18px] md:text-[20px] font-[600] text-gray-900 leading-tight">
                        {advantage.title}
                    </h3>
                </div>
                <p
                    className={`absolute inset-0 p-6 text-[14px] md:text-[15px] lg:text-[12px] xl:text-[15px] font-[400] leading-relaxed text-white transition-opacity duration-300 ${
                        isActive ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                >
                    {advantage.description}
                </p>
            </div>
        );
    };

    return (
        <section className="pb-[100px] md:pb-[200px] bg-white max-w-[90%] mx-auto">
            <div className="flex md:flex-row flex-col items-center justify-between gap-6 lg:gap-12 mb-[32px] md:mb-[64px]">
                <div className="flex flex-col w-full items-start">
                    <div className="text-[32px] md:text-[48px] lg:text-[64px] font-[500] leading-tight flex flex-col">
                        <h2 className="text-main-pink">{t("home-page.our-advantages-title-1")}</h2>
                        <h2 className="text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884]">
                            {t("home-page.our-advantages-title-2")}
                        </h2>
                    </div>
                </div>
                <div className="flex flex-col gap-4 lg:max-w-md w-full md:w-auto">
                    <Link href="/apply">
                        <button className="flex items-center justify-center w-full md:w-[200px] gap-2 h-[48px] md:h-[56px] px-6 rounded-full text-[#274DE0] border border-[#274DE0] text-[16px] font-[500] transition duration-300 hover:bg-[#274DE0] hover:text-white">
                            {t("home-page.our-advantages-join-us")}
                            <RxArrowTopRight className="w-6 h-6" />
                        </button>
                    </Link>
                </div>
            </div>
            <div
                ref={scrollRef}
                onMouseDown={handleScrollMouseDown}
                onMouseMove={handleScrollMouseMove}
                onMouseUp={endScrollDrag}
                onMouseLeave={endScrollDrag}
                className="flex w-full min-w-0 flex-nowrap gap-[10px] xl:gap-[20px] overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:grid lg:grid-cols-4 lg:grid-rows-2 lg:overflow-visible lg:snap-none lg:cursor-default cursor-grab active:cursor-grabbing select-none lg:select-auto"
            >
                {advantages.map((advantage, index) => renderCard(advantage, index))}
            </div>
        </section>
    );
};

export default OurAdvantages;
